export class DeepNestingError extends Error {
  constructor() {
    super("Object nesting is too deep");
    this.name = "DeepNestingError";
  }
}

type FlattenOptions = {
  delimiter?: string;
  targetKeys?: string[];
  depth?: number;
};

const MAX_RECURSION_DEPTH = 30;

/**
 * Converts a nested object (result of JSON.parse) into a flat structure
 * @param obj Target object to flatten (result of JSON.parse)
 * @param options.delimiter Key delimiter character (default: ".")
 * @param options.targetKeys List of keys to flatten (if empty, all keys are targeted)
 * @param options.depth Maximum depth to flatten (0 means unlimited)
 * @returns Flattened object
 * @throws {DeepNestingError} When object nesting is too deep
 *
 * @example
 * const obj = {
 *   a: {
 *     b: 1,
 *     c: 2
 *   }
 * };
 * const flattened = ObjectUtils.flatten(obj);
 * console.log(flattened);
 * // Output: { "a.b": 1, "a.c": 2 }
 */
function flattenKeys<T extends Record<string, unknown>>(obj: T, options: FlattenOptions = {}): Record<string, unknown> {
  const { delimiter = ".", targetKeys = [], depth = 0 } = options;
  const result: Record<string, unknown> = {};

  function shouldFlatten(key: string, currentLevel: number): boolean {
    if (targetKeys.length > 0 && !targetKeys.includes(key)) {
      return false;
    }
    if (depth > 0 && currentLevel >= depth) {
      return false;
    }
    return true;
  }

  function flattenRecursive(current: unknown, prefix: string, level: number): void {
    if (level > MAX_RECURSION_DEPTH) {
      throw new DeepNestingError();
    }

    if (typeof current !== "object" || current === null) {
      result[prefix] = current;
      return;
    }

    Object.entries(current as Record<string, unknown>).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}${delimiter}${key}` : key;

      if (typeof value === "object" && value !== null && shouldFlatten(key, level)) {
        flattenRecursive(value, newKey, level + 1);
      } else {
        result[newKey] = value;
      }
    });
  }

  flattenRecursive(obj, "", 0);
  return result;
}

/**
 * Converts a flattened object back to its nested structure
 * @param obj Flattened object to restore
 * @param options.delimiter Key delimiter character (default: ".")
 * @returns Nested object
 * @throws {DeepNestingError} When object nesting is too deep
 *
 * @example
 * const flat = {
 *   "a.b": 1,
 *   "a.c": 2
 * };
 * const nested = ObjectUtils.unflatten(flat);
 * console.log(nested);
 * // Output: { a: { b: 1, c: 2 } }
 */
function unflattenKeys(
  obj: Record<string, unknown>,
  options: Pick<FlattenOptions, "delimiter"> = {},
): Record<string, unknown> {
  const { delimiter = "." } = options;
  const result: Record<string, unknown> = {};

  Object.entries(obj).forEach(([key, value]) => {
    const parts = key.split(delimiter);
    let current = result;
    let level = 0;

    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      const isLast = i === parts.length - 1;

      if (level > MAX_RECURSION_DEPTH) {
        throw new DeepNestingError();
      }

      if (isLast) {
        current[part] = value;
      } else {
        // Handle array indices
        const nextPart = parts[i + 1];
        const isNextPartArrayIndex = /^\d+$/.test(nextPart);

        if (!(part in current)) {
          current[part] = isNextPartArrayIndex ? [] : {};
        }

        current = current[part] as Record<string, unknown>;
        level += 1;
      }
    }
  });

  return result;
}

export const ObjectUtils = {
  flattenKeys,
  unflattenKeys,
};
